import { Controller } from '@hotwired/stimulus'

/**
 * DrawerController
 *
 * Generic drawer shell. Handles:
 *   - open / close
 *   - backdrop
 *   - scroll lock
 *   - focus trap
 *   - Escape key
 *   - aria-expanded on the trigger
 *
 * Any feature-specific logic (e.g. panel switching in mobile-menu)
 * lives in its own controller mounted inside the drawer content.
 */
export default class DrawerController extends Controller {
  static targets = ['panel', 'backdrop', 'trigger']

  declare readonly panelTarget: HTMLElement
  declare readonly backdropTarget: HTMLElement
  declare readonly triggerTarget: HTMLElement

  declare readonly hasPanelTarget: boolean
  declare readonly hasBackdropTarget: boolean
  declare readonly hasTriggerTarget: boolean

  private previouslyFocused: HTMLElement | null = null

  private readonly focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ')

  // ── Lifecycle ────────────────────────────────────────────────────

  connect(): void {
    document.addEventListener('keydown', this.onKeydown)
  }

  disconnect(): void {
    document.removeEventListener('keydown', this.onKeydown)
    this.restoreScroll()
  }

  // ── Actions ──────────────────────────────────────────────────────

  open(): void {
    if (!this.hasPanelTarget) return

    const activeElement = document.activeElement
    this.previouslyFocused =
      activeElement instanceof HTMLElement ? activeElement : null

    // Remove hidden so the element is in the DOM before animating
    this.panelTarget.removeAttribute('hidden')

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.panelTarget.setAttribute('data-open', '')
        if (this.hasBackdropTarget) {
          this.backdropTarget.setAttribute('data-open', '')
        }
        this.focusFirst()
      })
    })

    this.lockScroll()

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute('aria-expanded', 'true')
    }

    this.dispatch('opened')
  }

  close(): void {
    if (!this.hasPanelTarget) return

    this.panelTarget.removeAttribute('data-open')

    if (this.hasBackdropTarget) {
      this.backdropTarget.removeAttribute('data-open')
    }

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute('aria-expanded', 'false')
    }

    // Restore focus to the element that opened the drawer
    this.previouslyFocused?.focus()
    this.previouslyFocused = null

    this.restoreScroll()

    // Hide after transition completes
    this.panelTarget.addEventListener(
      'transitionend',
      () => {
        if (!this.panelTarget.hasAttribute('data-open')) {
          this.panelTarget.setAttribute('hidden', '')
        }
      },
      { once: true },
    )

    this.dispatch('closed')
  }

  // ── Keyboard ────────────────────────────────────────────────────

  private onKeydown = (event: KeyboardEvent): void => {
    const isOpen =
      this.hasPanelTarget && this.panelTarget.hasAttribute('data-open')
    if (!isOpen) return

    if (event.key === 'Escape') {
      event.stopPropagation()
      this.close()
    }

    if (event.key === 'Tab') {
      this.handleTab(event)
    }
  }

  // ── Focus trap ────────────────────────────────────────────────

  private focusFirst(): void {
    const elements = this.getFocusableElements()
    elements[0]?.focus()
  }

  private handleTab(event: KeyboardEvent): void {
    const elements = this.getFocusableElements()
    if (elements.length === 0) return

    const first = elements[0]
    const last = elements[elements.length - 1]

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  private getFocusableElements(): HTMLElement[] {
    return Array.from(
      this.panelTarget.querySelectorAll<HTMLElement>(this.focusableSelectors),
    ).filter((el) => !el.closest('[hidden]'))
  }

  // ── Scroll lock ───────────────────────────────────────────────

  private lockScroll(): void {
    document.body.classList.add('drawer-open')
  }

  private restoreScroll(): void {
    document.body.classList.remove('drawer-open')
  }
}
